import { useEffect, useMemo, useState } from "react";
import { MdAutorenew } from "react-icons/md";
import { BrandButton } from "../components/ui/BrandButton";
import { SearchSelect } from "../components/form/SearchSelect";
import { useAppLoading } from "../context/AppLoadingContext";
import {
  getPredictionAudit,
  getPredictionAuditMeta,
} from "../services/adminPredictionAuditService";

const PAGE_SIZE = 20;

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("es-UY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatScore(homeGoals, awayGoals) {
  if (
    homeGoals === null ||
    homeGoals === undefined ||
    awayGoals === null ||
    awayGoals === undefined
  ) {
    return "—";
  }

  return `${homeGoals} - ${awayGoals}`;
}

function getResultLabel(resultCode) {
  switch (resultCode) {
    case "EXACT_SCORE":
      return "Resultado exacto";
    case "CORRECT_OUTCOME_AND_GOAL_DIFFERENCE":
      return "Acierto ganador y diferencia de goles";
    case "CORRECT_OUTCOME":
      return "Acierto ganador";
    case "INCORRECT":
      return "Incorrecto";
    default:
      return "Pendiente";
  }
}

function getFixtureLabel(fixture) {
  if (!fixture) {
    return "—";
  }

  return fixture.label || "Partido";
}

function getUserLabel(user) {
  if (!user) {
    return "—";
  }

  const username = user.username ? `@${user.username}` : "Usuario";
  return `${username} · ${user.name} ${user.lastName}`;
}

function getPointsBadgeClass(points, hasEvaluation) {
  if (!hasEvaluation) {
    return "bg-slate-100 text-slate-600";
  }

  if (points > 0) {
    return "bg-emerald-100 text-emerald-700";
  }

  return "bg-amber-100 text-amber-800";
}

export function AdminPredictionAuditPage() {
  const { trackAsync } = useAppLoading();
  const [meta, setMeta] = useState({ users: [], fixtures: [] });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [userId, setUserId] = useState("");
  const [fixtureId, setFixtureId] = useState("");
  const [page, setPage] = useState(1);
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isLoadingRows, setIsLoadingRows] = useState(true);
  const [metaError, setMetaError] = useState("");
  const [rowsError, setRowsError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadMeta = async () => {
      setIsLoadingMeta(true);
      setMetaError("");

      try {
        const response = await getPredictionAuditMeta();

        if (!isMounted) {
          return;
        }

        setMeta({
          users: response.users ?? [],
          fixtures: response.fixtures ?? [],
        });
      } catch (requestError) {
        if (isMounted) {
          setMetaError(requestError.message || "No pudimos cargar los filtros de auditoría.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingMeta(false);
        }
      }
    };

    void trackAsync(loadMeta);

    return () => {
      isMounted = false;
    };
  }, [trackAsync]);

  useEffect(() => {
    let isMounted = true;

    const loadRows = async () => {
      setIsLoadingRows(true);
      setRowsError("");

      try {
        const response = await getPredictionAudit({
          page,
          pageSize: PAGE_SIZE,
          userId,
          fixtureId,
        });

        if (!isMounted) {
          return;
        }

        setRows(response.rows ?? []);
        setPagination(
          response.pagination ?? {
            page,
            pageSize: PAGE_SIZE,
            total: 0,
            totalPages: 0,
          },
        );
      } catch (requestError) {
        if (isMounted) {
          setRowsError(requestError.message || "No pudimos cargar la auditoría de puntajes.");
          setRows([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingRows(false);
        }
      }
    };

    void trackAsync(loadRows);

    return () => {
      isMounted = false;
    };
  }, [fixtureId, page, trackAsync, userId]);

  const userOptions = useMemo(
    () => [
      { value: "", label: "Todos los usuarios" },
      ...meta.users.map((user) => ({
        value: user.id,
        label: user.label,
      })),
    ],
    [meta.users],
  );

  const fixtureOptions = useMemo(
    () => [
      { value: "", label: "Todos los partidos" },
      ...meta.fixtures.map((fixture) => ({
        value: fixture.id,
        label: fixture.label,
      })),
    ],
    [meta.fixtures],
  );

  const filterSummary = useMemo(() => {
    const selectedUser = meta.users.find((option) => option.id === userId) ?? null;
    const selectedFixture = meta.fixtures.find((option) => option.id === fixtureId) ?? null;
    const parts = [];

    //parts.push(selectedUser ? `Usuario: ${selectedUser.label}` : "Todos los usuarios");
    //parts.push(selectedFixture ? `Partido: ${selectedFixture.label}` : "Todos los partidos");

    return parts.join(" · ");
  }, [fixtureId, meta.fixtures, meta.users, userId]);

  const hasRows = rows.length > 0;
  const canGoBack = pagination.page > 1;
  const canGoNext = pagination.totalPages > 0 && pagination.page < pagination.totalPages;
  const error = metaError || rowsError;

  const handleUserChange = (event) => {
    setUserId(event.target.value);
    setPage(1);
  };

  const handleFixtureChange = (event) => {
    setFixtureId(event.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setUserId("");
    setFixtureId("");
    setPage(1);
  };

  return (
    <div className="grid gap-5">
      <section className="px-1 py-1 sm:px-2 sm:py-2">
        <h2 className="font-display text-3xl text-brand-primary sm:text-4xl">Puntajes</h2>
      </section>

      {error ? (
        <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="grid gap-4 rounded-[2rem] border border-brand-secondary/15 bg-white p-4 shadow-sm sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
          <SearchSelect
            label="Filtrar por usuario"
            name="userId"
            value={userId}
            onChange={handleUserChange}
            options={userOptions}
            placeholder={isLoadingMeta ? "Cargando usuarios..." : "Buscar usuario"}
            disabled={isLoadingMeta}
          />
          <SearchSelect
            label="Filtrar por partido"
            name="fixtureId"
            value={fixtureId}
            onChange={handleFixtureChange}
            options={fixtureOptions}
            placeholder={isLoadingMeta ? "Cargando partidos..." : "Buscar partido"}
            disabled={isLoadingMeta}
          />
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-brand-secondary">
              {isLoadingMeta ? "Cargando filtros..." : filterSummary}
            </p>
            <BrandButton
              type="button"
              onClick={handleClearFilters}
              variant="outline"
              radius="2xl"
              size="md"
              disabled={!userId && !fixtureId}
              className="lg:self-start"
            >
              <MdAutorenew className="h-5 w-5" />
              Limpiar filtros
            </BrandButton>
          </div>
        </div>
      </section>

      <section className="grid gap-3 rounded-[2rem] border border-brand-secondary/15 bg-brand-surface px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
          <span className="rounded-full bg-white px-3 py-1 font-semibold text-brand-primary shadow-sm">
            {pagination.total} registros
          </span>
          <span>
            Página {pagination.page} de {pagination.totalPages || 1}
          </span>
          <span>
            {hasRows ? `Mostrando ${rows.length} filas` : "Sin filas para mostrar"}
          </span>
        </div>
      </section>

      {isLoadingRows ? (
        <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-8 text-sm text-slate-600">
          Cargando auditoría de puntajes...
        </div>
      ) : null}

      {!isLoadingRows && !error ? (
        hasRows ? (
          <div className="overflow-hidden rounded-[2rem] border border-brand-secondary/15 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-[1280px] w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-brand-surface/95 text-left">
                  <tr className="border-b border-brand-secondary/15">
                    <th className="px-4 py-4 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-brand-primary">
                      Usuario
                    </th>
                    <th className="px-4 py-4 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-brand-primary">
                      Partido
                    </th>
                    <th className="px-4 py-4 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-brand-primary">
                      Predicción
                    </th>
                    <th className="px-4 py-4 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-brand-primary">
                      Resultado real
                    </th>
                    <th className="px-4 py-4 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-brand-primary">
                      Puntaje
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const hasEvaluation = Boolean(row.evaluation);
                    const points = Number(row.evaluation?.points ?? 0);

                    return (
                      <tr key={row.id} className="border-b border-brand-secondary/10 last:border-b-0">
                        <td className="px-4 py-4 align-top">
                          <div className="grid gap-1">
                            <span className="text-sm font-semibold text-slate-900">
                              {getUserLabel(row.user)}
                            </span>
                            <span className="text-xs text-slate-500">
                              Rol: {row.user?.role?.name ?? "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="grid gap-1">
                            <span className="text-sm font-semibold text-slate-900">
                              {getFixtureLabel(row.fixture)}
                            </span>
                            <span className="text-xs text-slate-500">
                              {formatDateTime(row.fixture?.kickoffAt)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-slate-700">
                          {formatScore(row.prediction?.homeGoals, row.prediction?.awayGoals)}
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-slate-700">
                          {formatScore(row.actualScore?.home, row.actualScore?.away)}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="grid gap-2">
                            <span
                              className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${getPointsBadgeClass(points, hasEvaluation)}`}
                            >
                              {hasEvaluation ? `${points} pts` : "Pendiente"}
                            </span>
                            <span className="text-xs text-slate-500">
                              {hasEvaluation ? getResultLabel(row.evaluation?.resultCode) : "Aún sin evaluación"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-10 text-center text-sm text-slate-500">
            No hay puntajes para mostrar con estos filtros.
          </div>
        )
      ) : null}

      {!isLoadingRows && !error ? (
        <div className="flex flex-col gap-3 rounded-[2rem] border border-brand-secondary/15 bg-brand-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-600">
            Mostrando {rows.length} de {pagination.total} registros
          </p>
          <div className="flex items-center gap-2">
            <BrandButton
              type="button"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={!canGoBack}
              variant="outline"
              radius="2xl"
              size="md"
              motion="subtle"
            >
              Anterior
            </BrandButton>
            <BrandButton
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={!canGoNext}
              variant="outline"
              radius="2xl"
              size="md"
              motion="subtle"
            >
              Siguiente
            </BrandButton>
          </div>
        </div>
      ) : null}
    </div>
  );
}
